import RestApi from '@/core/services/RestApi'
import { projectEndpoint } from '@/core/helpers'
import i18n from '@/core/utils/I18n'
import { RestApiPaginator } from '@/core/utils/RestApiPaginator'
import memoize from 'memoizee'

const fetchPages = memoize(
  (uid, start, limit, paginator) =>
    paginator.fetchPages({ start: start, limit: limit }),
  { promise: true, length: 3, maxAge: 10000 }
)

export default {
  namespace: true,

  state: {
    projects: [],
    project: null,
    user: null,
    features: null,
    integrations: {},
    provider: null,
    domains: null,
    pages: null,
    company: null,
    region: null,
    dateFilter: null,
    show_channel_platforms: null,
    cert_expiration_banner_close_count: null,
    company_channels: null,
    channels: null,
    platformChannels: null,
    platformAppsForChannels: null,
    applications: null,
    dashboardCampaign: null,
    tutorialCards: null,
    home: null,
    previewScreenSizeData: null,
    timezones: [],
    proxy: null,
    gamesToPurchase: [],
    activeGames: []
  },

  getters: {
    permissions(state) {
      if (state.user && state.user.permissions) {
        return state.user.permissions
      }

      return []
    },
    user(state) {
      return state.user ? state.user : undefined
    },
    project(state) {
      return state.project || undefined
    },
    userUid(state) {
      return state.user ? state.user.uid : undefined
    },

    projectId(state) {
      return state.project ? state.project.id : undefined
    },
    projectConfig(state, getters) {
      return state.projects.find(item => item.id === getters.projectId)
    },
    isProjectActive(state) {
      if (state.project) {
        return state.project.project_status === 'active'
      }
      return true
    },
    isProjectNotActive(state) {
      if (state.project) {
        return ['inactive', 'delete in progress'].includes(
          state.project.project_status
        )
      }
      return false
    },

    deleteTime(state) {
      return state.project ? state.project.delete_time : null
    },
    mxaDomain(state) {
      return state.domains ? state.domains.mxa : ''
    },
    userConfig(state) {
      return {
        dateFilter: state.dateFilter,
        channels: state.channels,
        show_channel_platforms: state.show_channel_platforms,
        cert_expiration_banner_close_count:
          state.cert_expiration_banner_close_count
      }
    },
    tutorialCards(state) {
      return state.tutorialCards
    },
    applications(state) {
      return state.applications
    },
    projectTimezone(state) {
      return state.project.timezone || 'UTC'
    },
    timezones(state, getters) {
      let projectTimezone = getters.projectTimezone
      return {
        '': i18n.t('CampaignForm_Common_UserTimezone'),
        [projectTimezone]: state.timezones[projectTimezone],
        ...state.timezones
      }
    },
    proxy(state) {
      return state.proxy || undefined
    },
    isOptimoveAvailable(state) {
      return (
        state.integrations &&
        state.integrations.hasOwnProperty('optimove') &&
        state.integrations.optimove &&
        state.integrations.optimove.active
      )
    },
    isSalesforceAvailable(state) {
      return (
        state.integrations &&
        state.integrations.hasOwnProperty('salesforce') &&
        state.integrations.salesforce &&
        state.integrations.salesforce.active
      )
    },
    isGoogleAdsAvailable(state) {
      return (
        state.integrations &&
        state.integrations.hasOwnProperty('googleads') &&
        state.integrations.googleads &&
        state.integrations.googleads.active
      )
    },
    isMetaAvailable(state) {
      return (
        state.integrations &&
        state.integrations.hasOwnProperty('meta') &&
        state.integrations.meta &&
        state.integrations.meta.active
      )
    },
    isBetssonAvailable(state) {
      return (
        state.integrations &&
        state.integrations.hasOwnProperty('betsson') &&
        state.integrations.betsson &&
        state.integrations.betsson.active
      )
    },
    isOasisAvailable(state) {
      return (
        state.integrations &&
        state.integrations.hasOwnProperty('oasis') &&
        state.integrations.oasis &&
        state.integrations.oasis.active
      )
    },
    previewScreenSize(state) {
      return state.previewScreenSizeData ? state.previewScreenSizeData : ''
    },
    isMicrositesAvailable(state) {
      return !!state.features.microsites
    },
    gamesToPurchase(state) {
      return state.gamesToPurchase
    },
    activeGames(state) {
      return state.activeGames
    }
  },

  mutations: {
    setProjects(state, projects) {
      state.projects = projects
    },

    setConfig(state, config) {
      state.user = config.user
      state.company = config.company
      state.domains = config.domains
      state.pages = config.pages
      state.provider = config.provider
      state.region = config.region
      state.home = config.home
      state.proxy = config.proxy
      state.features = config.features
      state.server = config.server
    },

    setProjectConfig(state, config) {
      state.project = config.project
      state.features = Object.assign({}, state.features, config.features)
      state.integrations = config.integrations
      state.applications = config.applications
      if (config.company) {
        state.company_channels = config.company.channels || null
      }
    },

    setPlatformChannels(state, channels) {
      const platformChannels = []
      const platformAppsForChannels = {}

      Object.keys(channels).forEach(channel => {
        const currentChannel = channels[channel]
        if (
          currentChannel.enabled &&
          (!currentChannel.hasOwnProperty('count') || currentChannel.count > 0)
        ) {
          platformChannels.push(channel)
        }
        switch (channel) {
          case 'app_push':
            if (channels[channel].android_push) {
              platformChannels.push('android_push')
            }
            if (channels[channel].ios_push) {
              platformChannels.push('ios_push')
            }
            platformAppsForChannels.android_push =
              (currentChannel.android_count || 0) > 0
            platformAppsForChannels.ios_push =
              (currentChannel.ios_count || 0) > 0
            break
          case 'inapp':
            if (channels[channel].inapp_android) {
              platformChannels.push('inapp_android')
            }
            if (channels[channel].inapp_ios) {
              platformChannels.push('inapp_ios')
            }
            platformAppsForChannels.inapp_android =
              (currentChannel.android_count || 0) > 0
            platformAppsForChannels.inapp_ios =
              (currentChannel.ios_count || 0) > 0
            break
          case 'inbox':
            platformAppsForChannels.inbox_android =
              (currentChannel.android_count || 0) > 0
            platformAppsForChannels.inbox_ios =
              (currentChannel.ios_count || 0) > 0
            platformAppsForChannels.inbox_web =
              (currentChannel.web_count || 0) > 0
            break
          case 'web_push':
          case 'onsite':
            platformAppsForChannels[channel] = (currentChannel.count || 0) > 0
            break
        }
      })

      state.platformChannels = platformChannels
      state.platformAppsForChannels = platformAppsForChannels
    },

    setUserPreferences(state, config) {
      state.dateFilter = config.dateFilter
      state.channels = config.channels
      state.show_channel_platforms = config.show_channel_platforms
      state.cert_expiration_banner_close_count =
        config.cert_expiration_banner_close_count
      // To be implemented when needed w/ other componenets (will need to create getter function)
      // state.applications = config.applications
      // state.dashboardCampaign = preferences.dashboardCampaign
    },
    setTutorialCards(state, config) {
      state.tutorialCards = config
    },
    setScreenSize(state, config) {
      state.previewScreenSizeData = config
    },
    setTimezones(state, config) {
      state.timezones = config
    },
    setGamesToPurchase(state, config) {
      state.gamesToPurchase = config
    },
    setActiveGames(state, config) {
      state.activeGames = config
    }
  },

  actions: {
    fetchProjects: (function() {
      // This allows us to keep the paginator in scope between requests
      let paginator

      return async function({ commit, dispatch, state }, filter, limit = 1) {
        if (paginator) {
          // If the filter changes we want to cancel previous fetch requests
          paginator.cancel()
        }

        if (typeof filter === 'string' && filter.trim() === '') {
          commit('setProjects', [])
          return void 0
        }

        let payload = {
          method: 'get',
          url: 'projects',
          params: {
            name: filter,
            sort: 'title',
            fields: 'id,name,description,company,provider,icon'
          }
        }

        paginator = new RestApiPaginator(payload)
        let uid = filter ? 'projects-' + filter : 'projects'

        try {
          let data = await fetchPages(uid, 1, limit, paginator)
          commit('setProjects', data)
        } catch (e) {
          if (!paginator.isCancel(e)) {
            throw e
          }
        }
      }
    })(),

    fetchConfig: async ({ commit }) => {
      await RestApi.get(`config`).then(({ data }) => {
        commit('setConfig', data)
      })
    },

    fetchProjectConfig: ({ commit }, projectId) => {
      return new Promise((resolve, reject) => {
        RestApi.get(`projects/${projectId}/config`)
          .then(({ data }) => {
            commit('setProjectConfig', data)
            resolve()
          })
          .catch(error => {
            commit('setProjectConfig', {})
            reject(error)
          })
      })
    },

    refreshPlatformChannels: ({ commit, getters }, projectId) => {
      if (!projectId) projectId = getters.projectId

      return new Promise((resolve, reject) => {
        RestApi.get(`projects/${projectId}?fields=channels&expand=channels`)
          .then(({ data }) => {
            commit('setPlatformChannels', data.channels)
            resolve()
          })
          .catch(error => {
            commit('setPlatformChannels', {})
            reject(error)
          })
      })
    },

    fetchUserPreferences: ({ commit }, projectId) => {
      return new Promise((resolve, reject) => {
        RestApi.get(`projects/${projectId}/user/config`)
          .then(({ data }) => {
            commit('setUserPreferences', data)
            resolve()
          })
          .catch(error => {
            commit('setUserPreferences', {})
            reject(error)
          })
      })
    },

    fetchTimezones: ({ commit }) => {
      return new Promise((resolve, reject) => {
        RestApi.get(`timezones`)
          .then(({ data }) => {
            commit('setTimezones', data)
            resolve()
          })
          .catch(error => {
            commit('setTimezones', {})
            reject(error)
          })
      })
    },

    reloadProjectConfig: ({ getters, dispatch }) => {
      return dispatch('fetchProjectConfig', getters.projectId)
    },

    saveUserPreferences: ({ commit, getters }, payload) => {
      return new Promise((resolve, reject) => {
        RestApi.patch(`projects/${getters.projectId}/user/config`, payload)
          .then(({ data }) => {
            // update store value
            commit('setUserPreferences', data)
            resolve()
          })
          .catch(error => {
            reject(error)
          })
      })
    },
    fetchTutorialCards: ({ commit, getters }) => {
      let tutorialCardsStr = localStorage.getItem(`tutorialCards`)
      let tutorialCards = {}
      try {
        tutorialCards = JSON.parse(tutorialCardsStr)
      } catch (e) {}
      commit('setTutorialCards', tutorialCards)
    },
    saveTutorialCard: ({ commit, getters }, payload) => {
      let tutorialCardsStr = localStorage.getItem(`tutorialCards`)
      let tutorialCards = {}
      if (tutorialCardsStr) {
        try {
          tutorialCards = JSON.parse(tutorialCardsStr)
        } catch (e) {}
      }

      tutorialCards = { ...tutorialCards, ...payload }
      localStorage.setItem(`tutorialCards`, JSON.stringify(tutorialCards))
      commit('setTutorialCards', tutorialCards)
    },
    keepScreenSize: ({ commit, getters }, payload) => {
      commit('setScreenSize', payload)
    },
    fetchGamesToPurchase({ commit }) {
      return new Promise((resolve, reject) => {
        RestApi(
          projectEndpoint({
            method: 'get',
            url: 'games',
            params: { expand: 'gameRegions' }
          })
        )
          .then(({ data }) => {
            commit('setGamesToPurchase', data)
            resolve()
          })
          .catch(error => {
            commit('setGamesToPurchase', {})
            reject(error)
          })
      })
    },
    fetchActiveGames({ commit }) {
      return new Promise((resolve, reject) => {
        RestApi(projectEndpoint({ method: 'get', url: 'active-games' }))
          .then(({ data }) => {
            commit('setActiveGames', data)
            resolve()
          })
          .catch(error => {
            commit('setActiveGames', {})
            reject(error)
          })
      })
    }
  }
}



// WEBPACK FOOTER //
// ./src/core/store/app.js